import { Button } from "@mui/material";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslations } from "use-intl";

import {
  FlightSearchTrackingEvents,
  rudderAnalytics,
  type SearchSource,
} from "@/app/lib/rudderstack";
import { Calendar } from "@/components/Calendar/Calendar";
import { PersistentDrawer } from "@/components/Drawer/PersistentDrawer";
import { TripType } from "@/types/enums";
import type { TFlightFormFieldValues } from "@/types/types";

import { DatesGuard } from "./DatesGuard";
import TripTypeRadioGroup from "./TripTypeRadioGroup";

const NUMBER_OF_MONTHS = 12;

type Props = {
  initialValues: TFlightFormFieldValues;
  analyticsSource: SearchSource;
  onConfirm: (values: TFlightFormFieldValues) => void;
};

function DatesSelection({ initialValues, analyticsSource, onConfirm }: Props) {
  const t = useTranslations();

  const methods = useForm<TFlightFormFieldValues>({
    defaultValues: initialValues,
  });

  const { control, handleSubmit, setValue } = methods;

  const [tripType, departure, ret] = useWatch({
    control,
    name: ["tripType", "dates.departure", "dates.return"],
  });

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onConfirm)}
        className="flex min-h-0 shrink grow basis-auto flex-col"
      >
        <Controller
          name="tripType"
          control={control}
          render={({ field }) => (
            <TripTypeRadioGroup
              value={field.value}
              onChange={(e) => {
                rudderAnalytics.track(
                  FlightSearchTrackingEvents.USER_CHANGED_TRIP_TYPE,
                  {
                    source: `${analyticsSource}_calendar`,
                    type: e.target.value as TripType,
                  },
                );
                field.onChange(e);
              }}
            />
          )}
        />

        <Calendar
          allowSameDayRangeSelection
          startField={{
            label: t("flights.search.departureDate"),
            value: departure,
            path: "dates.departure",
          }}
          endField={
            tripType === TripType.ROUND_TRIP
              ? {
                  label: t("flights.search.returnDate"),
                  value: ret,
                  path: "dates.return",
                }
              : undefined
          }
          setFieldValue={(path, value) => {
            setValue(path as "dates.departure" | "dates.return", value, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });
          }}
          disabledDates={{ before: new Date() }}
          startMonth={new Date()}
          numberOfMonths={NUMBER_OF_MONTHS}
        />
        <PersistentDrawer>
          <Button
            fullWidth
            variant="contained"
            size="medium"
            sx={{
              marginTop: "auto",
            }}
            type="submit"
          >
            {tripType === TripType.ROUND_TRIP
              ? t("calendar.confirmDates")
              : t("calendar.confirmDate")}
          </Button>
        </PersistentDrawer>
      </form>
      <DatesGuard />
    </FormProvider>
  );
}

export default DatesSelection;
